/**
 * Shared geometry + permission helpers for the workstream / phase "hide" eye
 * icons (MapWorkstreams.vue + MapPhases.vue). Kept in one place so the painters
 * (which draw the eye) and the hit-test (which makes it clickable) agree exactly.
 *
 * The Eye hides a workstream / phase (`toggleWorkstreamVisibility` /
 * `togglePhaseVisibility`); an EyeOff re-shows hidden ones — a placeholder row +
 * gap dividers for workstreams, and before/after markers for phases (mirroring
 * MapWorkstreams / MapPhases). Mirroring the SVG, only a workstream that isn't the
 * sole visible one can be hidden, and only the first/last visible phase (no gaps
 * in the middle).
 */
import { PHASE_Y, START_X, type useMapStore } from '@/store/remapStore';
import type { MapWorkstream, useIoStore } from '@/store/ioStore';

type IoStore = ReturnType<typeof useIoStore>;
type MapStore = ReturnType<typeof useMapStore>;

/** A run of consecutive hidden workstreams sitting after a visible one. */
export interface WorkstreamGap {
  workstreams: MapWorkstream[];
  afterWorkstreamId: string | null;
  isLastGap: boolean;
}

/** Glyph size + click tolerance (half-extent of the square hit box). */
export const EYE_SIZE = 14;
const EYE_HIT_HALF = 11;

/** A workstream can be hidden unless it is the only visible one. */
export function canHideWorkstream(io: IoStore, workstreamId: string): boolean {
  const first = io.firstVisibleWorkstream;
  const last = io.lastVisibleWorkstream;

  return !(workstreamId === first?.id && workstreamId === last?.id);
}

/** Only the first or last visible phase can be hidden (no middle gaps). */
export function canHidePhase(io: IoStore, phaseId: string): boolean {
  const first = io.firstVisiblePhase;
  const last = io.lastVisiblePhase;
  if (phaseId === first?.id && phaseId === last?.id) {
    return false;
  }

  return phaseId === first?.id || phaseId === last?.id;
}

/** Eye centre for a workstream row (sidebar gutter, near the row top). */
export function workstreamEyeCenter(
  map: MapStore,
  workstreamId: string,
): { x: number; y: number } {
  return { x: 17, y: map.workstreamY(workstreamId) + 16 };
}

/**
 * X centre of the workstream expand/collapse glyph (WI #5563): sits just right
 * of the eye (the eye is at x 17, so their square hit boxes — ±11 — don't
 * overlap). Exported so the DOM tooltip anchors off the same geometry.
 */
export const WORKSTREAM_EXPAND_X = 40;

/**
 * Expand / collapse glyph centre for a workstream row (WI #5563): same row-top
 * baseline as the eye. The expanded workstream shows a collapse glyph here;
 * other rows show an expand glyph on hover.
 */
export function workstreamExpandCenter(
  map: MapStore,
  workstreamId: string,
): { x: number; y: number } {
  return { x: WORKSTREAM_EXPAND_X, y: map.workstreamY(workstreamId) + 16 };
}

/** Info-icon size + click tolerance for the workstream description indicator. */
export const INFO_SIZE = 14;
const INFO_HIT_HALF = 9;

/**
 * Info-icon centre for a workstream row (sidebar right edge, vertically centred),
 * shown only when the workstream has a description (MapWorkstreams' `UiIcon
 * name="Info"`). Sits just inside the sidebar's right edge (START_X − 20), in the
 * 16px gutter right of the title. Shared so the painter and hit-test agree.
 */
export function workstreamInfoCenter(
  map: MapStore,
  workstreamId: string,
): { x: number; y: number } {
  return {
    x: START_X - 36,
    y: map.workstreamY(workstreamId) + map.workstreamHeight(workstreamId) / 2,
  };
}

/** Square hit-test around the workstream info icon. */
export function isInfoHit(
  px: number,
  py: number,
  center: { x: number; y: number },
): boolean {
  return (
    Math.abs(px - center.x) <= INFO_HIT_HALF &&
    Math.abs(py - center.y) <= INFO_HIT_HALF
  );
}

/** Eye centre for a phase ribbon (just inside the phase start, on the ribbon). */
export function phaseEyeCenter(phaseStartScreenX: number): {
  x: number;
  y: number;
} {
  return { x: phaseStartScreenX + 12, y: PHASE_Y + 7 };
}

/** Square hit-test around an eye centre. */
export function isEyeHit(
  px: number,
  py: number,
  center: { x: number; y: number },
): boolean {
  return (
    Math.abs(px - center.x) <= EYE_HIT_HALF &&
    Math.abs(py - center.y) <= EYE_HIT_HALF
  );
}

// --- Unhide affordances (EyeOff) — mirror MapPhases / MapWorkstreams ----------

/** Are there hidden phases before the first visible one? */
export function hasHiddenPhasesBefore(io: IoStore): boolean {
  const firstIdx = io.firstVisiblePhase?.idx;

  return (
    firstIdx !== undefined &&
    Array.from(io.hiddenPhases.values()).some((idx) => idx < firstIdx)
  );
}

/** Are there hidden phases after the last visible one? */
export function hasHiddenPhasesAfter(io: IoStore): boolean {
  const lastIdx = io.lastVisiblePhase?.idx;

  return (
    lastIdx !== undefined &&
    Array.from(io.hiddenPhases.values()).some((idx) => idx > lastIdx)
  );
}

/** EyeOff centre just left of the first visible phase (MapPhases). */
export function phaseUnhideBeforeCenter(firstPhaseScreenX: number): {
  x: number;
  y: number;
} {
  return { x: firstPhaseScreenX - 10, y: PHASE_Y - 10 };
}

/** EyeOff centre just right of the last visible phase (MapPhases). */
export function phaseUnhideAfterCenter(
  lastPhaseScreenX: number,
  lastPhaseWidthPx: number,
): { x: number; y: number } {
  return { x: lastPhaseScreenX + lastPhaseWidthPx + 2, y: PHASE_Y - 10 };
}

/**
 * Group consecutive hidden workstreams between visible ones (MapWorkstreams'
 * `hiddenWorkstreamGaps`). Each gap is drawn as a divider + EyeOff at the slot
 * where the run was hidden; clicking it re-shows that run.
 *
 * WI #5596: filter-hidden workstreams (`filterHidden`) are NOT gap members —
 * they have no reveal affordance by design — but they are not visible either,
 * so they must not anchor a gap divider (their row has collapsed away).
 */
export function hiddenWorkstreamGaps(
  io: IoStore,
  filterHidden?: ReadonlySet<string>,
): WorkstreamGap[] {
  const gaps: WorkstreamGap[] = [];
  const all = [...io.workstreams].sort((a, b) => a.position - b.position);
  let current: MapWorkstream[] = [];
  let lastVisibleId: string | null = null;

  for (const ws of all) {
    if (ws.isHiddenOnMap) {
      current.push(ws);
    } else if (filterHidden?.has(ws.id)) {
      continue;
    } else {
      if (current.length > 0) {
        gaps.push({
          workstreams: current,
          afterWorkstreamId: lastVisibleId,
          isLastGap: false,
        });
        current = [];
      }
      lastVisibleId = ws.id;
    }
  }
  if (current.length > 0) {
    gaps.push({
      workstreams: current,
      afterWorkstreamId: lastVisibleId,
      isLastGap: true,
    });
  }

  return gaps;
}

/** Bottom-of-the-row baseline a gap divider sits on (MapWorkstreams.getGapBaseY). */
function gapBaseY(map: MapStore, io: IoStore, gap: WorkstreamGap): number {
  if (gap.afterWorkstreamId) {
    return (
      map.workstreamY(gap.afterWorkstreamId) +
      map.workstreamHeight(gap.afterWorkstreamId)
    );
  }

  return map.workstreamY(io.firstVisibleWorkstream.id);
}

/** The two divider line Ys for a gap (MapWorkstreams.getGapLineY1/2). */
export function gapLineYs(
  map: MapStore,
  io: IoStore,
  gap: WorkstreamGap,
): [number, number] {
  const base = gapBaseY(map, io, gap);
  if (gap.afterWorkstreamId) {
    return [base + (gap.isLastGap ? -5 : -2), base + (gap.isLastGap ? -1 : 2)];
  }

  return [base - 2, base + 2];
}

/** EyeOff centre for a gap (MapWorkstreams.getGapIconY + the x=10/20 box). */
export function gapEyeCenter(
  map: MapStore,
  io: IoStore,
  gap: WorkstreamGap,
): { x: number; y: number } {
  const base = gapBaseY(map, io, gap);
  const iconY = gap.afterWorkstreamId
    ? base + (gap.isLastGap ? -18 : -10)
    : base - 12;

  return { x: 17, y: iconY + 10 };
}
